/**
 * Route and stage selection.
 *
 * The slug is read from the address and passed to the API. It identifies a
 * portal; it authorises nothing, and this application treats it as a label it
 * was handed rather than as proof of anything.
 */

import { CodeStep } from './components/CodeStep';
import { EmailGate } from './components/EmailGate';
import { Notice } from './components/Notice';
import { PageFrame } from './components/PageFrame';
import { PortalView } from './components/PortalView';
import {
  NO_SLUG_MESSAGE,
  PORTAL_LOAD_FAILURE_MESSAGE,
  PORTAL_LOAD_RETRY,
  PORTAL_TITLE,
} from './copy';
import { readSlug, usePortal } from './hooks/usePortal';

export function App() {
  const slug = readSlug(window.location.pathname);

  if (slug === null) {
    return (
      <PageFrame>
        <section className="panel">
          <h1>{PORTAL_TITLE}</h1>
          <p className="lede">{NO_SLUG_MESSAGE}</p>
        </section>
      </PageFrame>
    );
  }

  return <PortalRoute slug={slug} />;
}

function PortalRoute({ slug }: { slug: string }) {
  const portal = usePortal(slug);

  switch (portal.stage.name) {
    case 'loading':
      return <PageFrame>{null}</PageFrame>;

    case 'gate':
      return (
        <PageFrame>
          <EmailGate notice={portal.notice} busy={portal.busy} onSubmit={portal.sendCode} />
        </PageFrame>
      );

    case 'code':
      return (
        <PageFrame>
          <CodeStep
            email={portal.stage.email}
            notice={portal.notice}
            busy={portal.busy}
            onSubmit={portal.verifyCode}
            onBack={portal.returnToGate}
          />
        </PageFrame>
      );

    case 'portal':
      return (
        <PageFrame customerName={portal.stage.data.customer.name}>
          <PortalView
            data={portal.stage.data}
            notice={portal.notice}
            busy={portal.busy}
            ndaOpen={portal.ndaFor !== null || portal.ndaForCredential !== null}
            openingId={portal.openingId}
            access={portal.access}
            onOpen={portal.openDocument}
            onAcceptNda={portal.acceptNda}
            onDismissNda={portal.dismissNda}
            onRevealCredential={portal.revealCredential}
            onRequestCredentialNda={portal.requestCredentialNda}
          />
        </PageFrame>
      );

    case 'portal-error':
      return (
        <PageFrame customerName={portal.stage.customer.name}>
          <section className="panel">
            <h1>{PORTAL_TITLE}</h1>
            <Notice message={PORTAL_LOAD_FAILURE_MESSAGE} />
            <button
              type="button"
              className="button-primary"
              onClick={portal.retryLoad}
              disabled={portal.busy}
            >
              {PORTAL_LOAD_RETRY}
            </button>
          </section>
        </PageFrame>
      );
  }
}
